import React from "react";
import {
  getTransactionTypeAboutText,
  getTransactionTypeArray,
  getTransactionTypeLabel,
  TxKeys,
} from "../../pages/transactionDetails/TransactionConfig";
import TransactionInputOutput from "../../pages/transactionDetails/TransactionInputOutput.component";
import CopyTextComponent from "../copyText/CopyText.component";
import { Paper, Tooltip } from "@mantine/core";
import styles from "../../pages/transactionDetails/TransactionDetailsPage.module.scss";
import { TransactionResponse } from "../../models/Response";
import { formatTime, useAvailableTime } from "../../utils/formatTime";
import { TransactionDetail } from "../../models/TransactionDetail";
import { useNavigate } from "react-router-dom";
import { IconHelpSquareRounded } from "@tabler/icons-react";
import { formatAddress, formatAmountWithLabel } from "../../utils/formatter";
import { TransactionType } from "../../models/enums";
import { Transaction, TransactionInOut } from "../../models/Transaction";
import Button from "../Button/Button.component";
import ToggleUnixTime from "../toggleUnixTime/ToggleUnixTime.component";
import DetailsRowComponent from "../detailsRow/detailsRow.component";

export function calculateDateDifference(
  startTime: Date,
  endTime: Date,
  inDays = false,
): number {
  try {
    const diffInMs = endTime.getTime() - startTime.getTime();
    return inDays ? diffInMs / (1000 * 60 * 60 * 24) : diffInMs;
  } catch (err: any) {
    console.error("Error at calculateDateDifference() ", err.message);
    return 0;
  }
}

export function getDaysRemaining(
  endTime: string | null,
  withoutUnit = false,
  withoutLabel = false,
): JSX.Element {
  if (!endTime) return <span>Not Available</span>;

  const endDate = new Date(endTime);
  const currentDate = new Date();

  if (endDate.getTime() < currentDate.getTime()) {
    return <span>Finished</span>;
  }

  const dateRemaining = Math.floor(
    calculateDateDifference(currentDate, endDate, true),
  );

  if (dateRemaining > 0)
    return (
      <span>
        {withoutUnit
          ? dateRemaining
          : `${dateRemaining} day${dateRemaining > 1 ? "s" : ""} ${withoutLabel ? "" : "remaining"}`}
      </span>
    );
  else if (dateRemaining == 0) return <span>0 days</span>;
  else return <span>Expired</span>;
}

export function calculateAmountValue(tnxObj: Transaction): number {
  let value: number | null = null;
  if (tnxObj.type === TransactionType.EXPORT_TX) {
    const filteredOutputs = tnxObj.outputs.filter((output) => output.type === "EXPORT");
    value = sumOutputs(filteredOutputs);
  } else if (tnxObj.type === TransactionType.IMPORT_TX) {
    value = sumOutputs(tnxObj.outputs);
  } else if (
    tnxObj.type === TransactionType.ADD_DELEGATOR_TX ||
    tnxObj.type === TransactionType.ADD_VALIDATOR_TX ||
    tnxObj.type === TransactionType.ADD_PERMISSIONLESS_DELEGATOR_TX ||
    tnxObj.type === TransactionType.ADD_PERMISSIONLESS_VALIDATOR_TX
  ) {
    const filteredOutputs = tnxObj.outputs.filter((output) => output.type === "STAKE");
    value = sumOutputs(filteredOutputs);
  } else if (tnxObj.type === TransactionType.BASE_TX) {
    const fromAddress = tnxObj.inputs[0].address;
    const filteredOutputs = tnxObj.outputs.filter((output) => output.address !== fromAddress);
    value = sumOutputs(filteredOutputs);
  } else {
    value = tnxObj.outputs[0]?.amount ?? 0;
  }
  return value !== null ? Number(value) : 0;
}


/**
 * Calculates the sum of the `amount` values in the given array of outputs,
 * ensuring that each `index` value is only counted once.
 *
 * @param {TransactionInOut[]} outputs - An array of transaction objects,
 * each containing an `index` (unique identifier) and `amount`.
 * @return {number} The total sum of the `amount` values, excluding duplicates
 * based on the `index`.
 */
function sumOutputs(outputs: TransactionInOut[]): number {
  const seenIndices = new Set<number>();
  let value = 0;

  outputs.forEach((output) => {
    if (!seenIndices.has(output.index)) {
      seenIndices.add(output.index);
      value += output.amount;
    }
  });
  return value;
}

export function calculateWeightAmount(tnxObj: Transaction): number {
  let value: number | null = null;
  value = tnxObj.weight;
  return value !== null ? Number(value) : 0;
}

export function calculateFees(tnxObj: Transaction): number {
  // sumOutputs works the same for inputs and for outputs
  const inputSum = sumOutputs(tnxObj.inputs);
  const outputSum = sumOutputs(tnxObj.outputs);

  return inputSum - outputSum;
}

export function getTransactionValue(
  tnxObj: Transaction,
  propertyKey: string,
  exportTxNextId?: string,
): React.ReactNode {
  switch (propertyKey) {
    case TxKeys.FEE: {
      if (tnxObj.inputs && tnxObj.outputs) {
        const res = calculateFees(tnxObj);
        return (
          <span>{formatAmountWithLabel(res ? Number(res) : 0, true)}</span>
        );
      }
      return 0;
    }
    case TxKeys.DURATION: {
      if (!tnxObj.endTime || !tnxObj.startTime)
        return <span>Not Available</span>;
      const timeDiff: number = calculateDateDifference(
        new Date(tnxObj.startTime),
        new Date(tnxObj.endTime),
      );
      if (!timeDiff) return <span>0</span>;
      return formatTime(timeDiff);
    }
    case TxKeys.DAYS_REMAINING: {
      return getDaysRemaining(tnxObj.endTime);
    }
    case TxKeys.INPUT_OUTPUT:
      return (
        <TransactionInputOutput
          inputs={tnxObj.inputs}
          outputs={tnxObj.outputs}
          nextId={exportTxNextId}
        />
      );

    case TxKeys.START_TIME:
    case TxKeys.END_TIME:
    case TxKeys.TIMESTAMP: {
      const key: "chainTime" | "endTime" | "startTime" = propertyKey;

      let value: string | null;
      if (key === "chainTime") {
        value = useAvailableTime(tnxObj);
      } else {
        value = tnxObj[key];
      }
      if (value) {
        return <ToggleUnixTime time={value} />;
      }

      return <span>No date provided</span>;
    }
    case TxKeys.TO:
    case TxKeys.FROM: {
      let address;
      switch (propertyKey) {
        case TxKeys.TO:
          if (tnxObj.type === TransactionType.BASE_TX) {
            const fromAddress = tnxObj.inputs[0].address;
            address = tnxObj.outputs.find((output) => {
                return output.address !== fromAddress;
              },
            )?.address ?? "";
          } else {
            address = tnxObj.outputs[0].address;
          }
          break;
        case TxKeys.FROM:
          address = tnxObj.inputs[0].address;
          break;
      }
      return (
        <>
          {address == "" ? (
            <span>Not Available</span>
          ) : (
            <CopyTextComponent
              iconColor="gray"
              iconBackgroundColor="inherit"
              align="left"
              text={formatAddress(address ?? "", 25, true)}
              value={address ?? ""}
              weight={"bold"}
              link={`/address/${address?.toString()}`}
              primaryColor={false}
            ></CopyTextComponent>
          )}
        </>
      );
    }
    case TxKeys.NODE_ID:
    case TxKeys.TX_ID: {
      let textValue = "";
      if (propertyKey === TxKeys.TX_ID) {
        textValue = tnxObj.txID;
      } else {
        textValue = tnxObj[propertyKey];
      }

      return (
        <CopyTextComponent
          iconColor="gray"
          iconBackgroundColor="inherit"
          align="left"
          text={formatAddress(textValue, 25, true)}
          value={textValue}
          weight={"bold"}
          link={
            propertyKey === TxKeys.NODE_ID
              ? `/validator/${textValue}`
              : undefined
          }
          primaryColor={propertyKey !== TxKeys.NODE_ID}
        ></CopyTextComponent>
      );
    }
    case TxKeys.TYPE: {
      let textValue;
      if (tnxObj.type === TransactionType.IMPORT_TX) {
        textValue = "Import to P-Chain";
      } else if (tnxObj.type === TransactionType.EXPORT_TX) {
        textValue = "Export from P-Chain";
      } else {
        textValue = getTransactionTypeLabel(tnxObj.type as TransactionType);
      }
      return (
        <div className={styles.typeWithHelp}>

          <span>{textValue}</span>

          <Tooltip
            label={getTransactionTypeAboutText(tnxObj.type)}
            position="top"
            events={{ hover: true, focus: false, touch: true }}
          >
            <IconHelpSquareRounded size={20} />
          </Tooltip>
        </div>
      );
    }
    case TxKeys.AMOUNT: {
      return <>{formatAmountWithLabel(calculateAmountValue(tnxObj))}</>;
    }
    case TxKeys.WEIGHT: {
      return <>{formatAmountWithLabel(calculateWeightAmount(tnxObj))}</>;
    }
    case TxKeys.FEE_PERCENTAGE: {
      const feePercentage = tnxObj.feePercentage as number;
      return (
        <span>
          {tnxObj.outputs ? feePercentage / 1e4 + "%" : "Not Available"}
        </span>
      );
    }
    default:
      return <>{tnxObj[propertyKey]}</>;
  }
}

export default function TransactionDetailsTable(
  {
    transaction,
    exportTransactionId,
  }: {
    transaction: TransactionResponse | undefined;
    exportTransactionId?: string
  }) {
  // const { transactionId } = useParams();
  const navigate = useNavigate();

  const goHome = () => {
    navigate("/");
  };

  return (
    <>
      <Paper>
        {transaction && transaction.data ? (
          <>
            {/* Render row with information block */}
            {getTransactionTypeArray(transaction.data.type).map(
              (element: TransactionDetail, index: number) => {
                console.log("element", element, index);
                return (
                  <React.Fragment key={index}>
                    <DetailsRowComponent isInputOutput={element.key === TxKeys.INPUT_OUTPUT} label={element.label}
                                         info={element.info}
                                         value={getTransactionValue(transaction.data, element.key, exportTransactionId)}></DetailsRowComponent>
                  </React.Fragment>
                );
              },
            )}
          </>
        ) : (
          //TODO: add as standalone component
          <div className={styles.errorLabel}>
            <span>Sorry.</span>
            <span>
              We were unable to locate this address or transaction hash.
            </span>
            <div>
              <Button addStyles={["invert"]} onClick={goHome}>
                View latest blocks
              </Button>
            </div>
          </div>
        )}
      </Paper>
    </>
  );
}
